import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { toast as sonner } from '@spartan-ng/brain/sonner';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../../dialog-templates/confirm-dialog/confirm-dialog.component';
import { getApiErrorMessage, hasApiErrorCode } from '../../../services/api-error-message';
import { BucketService } from '../../../services/bucket.service';
import {
  CategorizationRule,
  CategorizationRuleService,
  CategorizationRuleSuggestion,
  CategorizationRuleTransactionType,
  MerchantSuggestion,
} from '../../../services/categorization-rule.service';
import { DrawerService, TransactionRuleDrawerData } from '../../../services/drawer.service';
import { SessionService } from '../../../services/session.service';
import { TransactionService } from '../../../services/transaction.service';
import { MerchantAvatarComponent } from '../../../shared/merchant-avatar/merchant-avatar.component';
import { DRAWER_DATA } from '../../../shared/injector-tokens';
import {
  RuleConflictDetails,
  amountRangeLabel,
  suggestionMovesBudget,
  suggestionChangesAmounts,
  criteriaMatchTransaction,
  eligibleBucketsForRuleType,
  formatRuleAmount,
  isRuleBackfillEligible,
  merchantKey,
  pluralize,
  ruleConflictDetailsFromError,
  ruleEffectiveState,
  ruleEffectiveStateLabel,
  ruleTypeLabel,
  transactionRuleType,
} from '../../../transaction-rules/transaction-rule-display';
import type { Bucket, Transaction } from '../../../types';
import { UiButtonDirective } from '../../../ui/button/button.directive';
import { UiComboboxComponent } from '../../../ui/combobox/combobox.component';
import type { UiComboboxOption } from '../../../ui/combobox/combobox-option';
import { UiCurrencyInputComponent } from '../../../ui/currency-input/currency-input.component';
import { UiDialogService } from '../../../ui/dialog/dialog.service';
import { UiFieldComponent } from '../../../ui/field/field.component';
import { UiIconComponent } from '../../../ui/icon/icon.component';
import { UiInputDirective } from '../../../ui/input/input.directive';
import {
  UiDropdownMenuDirective,
  UiDropdownMenuItemDirective,
  UiDropdownMenuTriggerDirective,
} from '../../../ui/menu/menu.directive';
import { UiHeadingDirective } from '../../../ui/heading/heading.directive';
import { DrawerFooterComponent } from '../../drawer-layout/drawer-footer.component';
import { DrawerLayoutComponent } from '../../drawer-layout/drawer-layout.component';
import { UiToastService } from '../../../ui/toast/toast.service';
import { UiToggleGroupComponent } from '../../../ui/toggle-group/toggle-group.component';
import type { UiToggleGroupOption } from '../../../ui/toggle-group/toggle-group-option';
import { categorizationRuleQueryKeys } from '../../../services/query-keys';
import { TransactionRuleSuggestionLookupService } from '../../../transaction-rules/transaction-rule-suggestion-lookup.service';

export type TransactionRuleDrawerMode = 'suggestion' | 'suggestion-from-transaction' | 'edit' | 'new';
type AmountMode = 'any' | 'range';

interface RuleFormState {
  merchant: string;
  transactionType: CategorizationRuleTransactionType;
  amountMode: AmountMode;
  amountMin: number | null;
  amountMax: number | null;
  bucketId: string | null;
}

const EMPTY_FORM: RuleFormState = {
  merchant: '',
  transactionType: 'EXPENSE',
  amountMode: 'any',
  amountMin: null,
  amountMax: null,
  bucketId: null,
};

@Component({
  selector: 'app-transaction-rule-drawer-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './transaction-rule-drawer-wrapper.component.html',
  imports: [
    CurrencyPipe,
    DatePipe,
    FormsModule,
    MerchantAvatarComponent,
    UiButtonDirective,
    UiComboboxComponent,
    UiCurrencyInputComponent,
    UiFieldComponent,
    UiIconComponent,
    UiDropdownMenuDirective,
    UiDropdownMenuItemDirective,
    UiDropdownMenuTriggerDirective,
    UiInputDirective,
    UiHeadingDirective,
    UiToggleGroupComponent,
    DrawerLayoutComponent,
    DrawerFooterComponent,
  ],
})
export class TransactionRuleDrawerWrapperComponent {
  readonly data = inject<TransactionRuleDrawerData>(DRAWER_DATA);
  private readonly drawer = inject(DrawerService);

  private readonly categorizationRuleService = inject(CategorizationRuleService);
  private readonly bucketService = inject(BucketService);
  private readonly transactionService = inject(TransactionService);
  private readonly sessionService = inject(SessionService);
  private readonly dialog = inject(UiDialogService);
  private readonly toast = inject(UiToastService);
  private readonly ruleSuggestions = inject(TransactionRuleSuggestionLookupService);

  /** The drawer can be reached by URL, so it re-checks the account flag itself. */
  readonly featureEnabled = this.ruleSuggestions.featureEnabled;

  readonly mode: TransactionRuleDrawerMode;

  readonly transactionTypeOptions: UiToggleGroupOption<CategorizationRuleTransactionType>[] = [
    { label: 'Expense', value: 'EXPENSE', testId: 'transaction-rule-drawer-type-expense' },
    { label: 'Income', value: 'INCOME', testId: 'transaction-rule-drawer-type-income' },
  ];
  readonly amountModeOptions: UiToggleGroupOption<AmountMode>[] = [
    { label: 'Any amount', value: 'any', testId: 'transaction-rule-drawer-amount-any' },
    { label: 'Range', value: 'range', testId: 'transaction-rule-drawer-amount-range' },
  ];

  readonly rulesQuery = injectQuery(() => ({
    ...this.categorizationRuleService.rulesQueryOptions(),
    enabled: this.featureEnabled(),
  }));
  readonly suggestionsQuery = injectQuery(() => ({
    ...this.categorizationRuleService.suggestionsQueryOptions('ACTIVE'),
    enabled: this.featureEnabled() && !!this.data.suggestionId,
  }));
  readonly bucketsQuery = injectQuery(() => ({
    ...this.bucketService.allBucketsQueryOptions(),
    enabled: this.featureEnabled(),
  }));
  readonly transactionQuery = injectQuery(() => ({
    ...this.transactionService.transactionDetailQueryOptions(this.data.transactionId ?? ''),
    enabled: this.featureEnabled() && !!this.data.transactionId,
  }));
  readonly backfillPreviewQuery = injectQuery(() => ({
    ...this.categorizationRuleService.backfillPreviewQueryOptions(this.data.ruleId ?? ''),
    enabled: this.featureEnabled() && !!this.data.ruleId,
  }));

  readonly rule = computed<CategorizationRule | null>(
    () => this.rulesQuery.data()?.find(rule => rule.id === this.data.ruleId) ?? null,
  );
  readonly suggestionSnapshot = signal<CategorizationRuleSuggestion | null>(null);
  readonly proposal = computed(() => this.suggestion()?.proposal ?? null);
  /** The update moves the rule to another budget, not only its amounts. */
  readonly proposalMovesBudget = computed(() => suggestionMovesBudget(this.suggestion()));
  readonly proposalChangesAmounts = computed(() => suggestionChangesAmounts(this.suggestion()));
  readonly suggestion = computed<CategorizationRuleSuggestion | null>(
    () =>
      this.suggestionSnapshot() ??
      this.suggestionsQuery.data()?.find(suggestion => suggestion.id === this.data.suggestionId) ??
      null,
  );
  readonly transaction = computed<Transaction | null>(() => this.transactionQuery.data() ?? null);
  readonly buckets = computed<Bucket[]>(() => this.bucketsQuery.data()?.data ?? []);
  readonly bucketsById = computed(() => new Map(this.buckets().map(bucket => [bucket.id, bucket] as const)));

  /**
   * Saved logo first; a drawer opened from a transaction can fall back to that
   * transaction's own, but only while the merchant still matches it (a new rule's
   * merchant is editable).
   */
  readonly merchantLogoUrl = computed(() => {
    const saved = this.rule()?.merchantLogoUrl ?? this.suggestion()?.merchantLogoUrl;
    if (saved) return saved;
    const current = merchantKey(this.form().merchant);
    if (!current) return null;
    // A matching autocomplete entry says exactly what saving stores (including no logo),
    // so it wins over the opened-from transaction's possibly older logo.
    const match = [this.pickedMerchant(), ...(this.merchantAutocompleteQuery.data() ?? [])].find(
      merchant => merchant != null && merchantKey(merchant.name) === current,
    );
    if (match) return match.logoUrl;
    const transaction = this.transaction();
    return transaction && merchantKey(transaction.merchant) === current
      ? (transaction.merchantMeta?.logoUrl ?? null)
      : null;
  });

  readonly evidenceIds = computed(() => this.suggestion()?.evidence.sampleTransactionIds.slice(0, 3) ?? []);
  readonly evidenceQuery = injectQuery(() => ({
    queryKey: [...categorizationRuleQueryKeys.all, 'suggestion-evidence', this.evidenceIds()] as const,
    queryFn: () => Promise.all(this.evidenceIds().map(id => this.transactionService.fetchTransactionRequest(id))),
    enabled: this.featureEnabled() && this.evidenceIds().length > 0,
    staleTime: 60_000,
  }));
  readonly evidenceTransactions = computed<Transaction[]>(() =>
    [...(this.evidenceQuery.data() ?? [])].sort((left, right) => right.date.localeCompare(left.date)),
  );
  readonly evidenceExpanded = signal(true);

  readonly form = signal<RuleFormState>(EMPTY_FORM);
  readonly formSeeded = signal(false);
  readonly saving = signal(false);
  readonly conflict = signal<RuleConflictDetails | null>(null);
  readonly errorMessage = signal<string | null>(null);
  /** The server rejected the accept because the recommendation changed; offer a refresh, not an error. */
  readonly suggestionStale = signal(false);
  /** Refresh found no recommendation yet because the server is still recomputing it. */
  readonly suggestionUpdating = signal(false);
  readonly amountError = signal<string | null>(null);

  readonly merchantSearch = new Subject<string>();
  readonly merchantSearchTerm = toSignal(this.merchantSearch.pipe(debounceTime(150), distinctUntilChanged()), {
    initialValue: '',
  });
  readonly merchantListOpen = signal(false);
  /** The autocomplete entry last chosen, kept so its logo survives the next search. */
  readonly pickedMerchant = signal<MerchantSuggestion | null>(null);
  readonly merchantAutocompleteQuery = injectQuery(() => ({
    ...this.categorizationRuleService.merchantAutocompleteQueryOptions(this.merchantSearchTerm()),
    enabled:
      this.featureEnabled() &&
      this.mode === 'new' &&
      this.merchantListOpen() &&
      this.merchantSearchTerm().trim().length > 0,
  }));
  readonly merchantMatches = computed(() => {
    const current = merchantKey(this.form().merchant);
    return (this.merchantAutocompleteQuery.data() ?? []).filter(merchant => merchantKey(merchant.name) !== current);
  });

  readonly eligibleBuckets = computed(() => eligibleBucketsForRuleType(this.buckets(), this.form().transactionType));
  readonly bucketOptions = computed<UiComboboxOption<Bucket>[]>(() =>
    this.eligibleBuckets().map(bucket => ({ key: bucket.id, label: bucket.name, value: bucket })),
  );
  readonly selectedBucketOption = computed<UiComboboxOption<Bucket> | null>(() => {
    const bucketId = this.form().bucketId;
    return this.bucketOptions().find(option => option.key === bucketId) ?? null;
  });
  readonly selectedBucketName = computed(() => this.selectedBucketOption()?.label ?? null);
  readonly criteria = computed(() => {
    const form = this.form();
    return {
      merchant: form.merchant.trim(),
      transactionType: form.transactionType,
      amountMin: form.amountMode === 'range' ? form.amountMin : null,
      amountMax: form.amountMode === 'range' ? form.amountMax : null,
    };
  });
  /** The opened-from transaction is one the server backfill can actually sort. */
  readonly transactionSortable = computed(() => {
    const transaction = this.transaction();
    return !!transaction && this.mode === 'suggestion-from-transaction' && isRuleBackfillEligible(transaction);
  });
  readonly transactionExcludedByEdit = computed(() => {
    const transaction = this.transaction();
    if (!transaction || !this.transactionSortable()) return false;
    if (!this.criteria().merchant) return false;
    return !criteriaMatchTransaction(this.criteria(), transaction);
  });
  readonly formValid = computed(() => {
    const form = this.form();
    if (!form.merchant.trim() || !form.bucketId) return false;
    if (form.amountMode === 'range') {
      if (form.amountMin == null || form.amountMax == null) return false;
      if (form.amountMin < 0 || form.amountMin > form.amountMax) return false;
    }
    return true;
  });
  readonly formChangedFromSuggestion = computed(() => {
    const suggestion = this.suggestion();
    if (!suggestion) return false;
    const criteria = this.criteria();
    return (
      merchantKey(criteria.merchant) !== merchantKey(suggestion.merchant) ||
      criteria.transactionType !== suggestion.transactionType ||
      criteria.amountMin !== suggestion.amountMin ||
      criteria.amountMax !== suggestion.amountMax ||
      this.form().bucketId !== suggestion.bucketId
    );
  });

  readonly ruleState = computed(() => {
    const rule = this.rule();
    return rule ? ruleEffectiveState(rule, this.bucketsById().get(rule.bucketId)) : null;
  });
  readonly ruleStateLabel = computed(() => {
    const state = this.ruleState();
    return state ? ruleEffectiveStateLabel(state) : null;
  });
  readonly backfillCount = computed(() => this.backfillPreviewQuery.data()?.totalRecords ?? null);

  readonly isLoading = computed(() => {
    if (!this.featureEnabled()) return false;
    if (this.mode === 'edit') return this.rulesQuery.isPending() || this.bucketsQuery.isPending();
    if (this.data.suggestionId) return this.suggestionsQuery.isPending() || this.bucketsQuery.isPending();
    // A transaction-backed drawer must not expose the form until its seed source has loaded.
    if (this.data.transactionId) return this.transactionQuery.isPending() || this.bucketsQuery.isPending();
    return this.bucketsQuery.isPending();
  });
  readonly missingSource = computed(() => {
    if (!this.featureEnabled()) return true;
    if (this.isLoading()) return false;
    if (this.mode === 'edit') return !this.rule();
    if (this.data.suggestionId) return !this.suggestion();
    if (this.data.transactionId) return !this.transaction();
    return false;
  });

  readonly headerEyebrow = computed(() => {
    if (this.proposal()?.action === 'update') return 'Rule update';
    switch (this.mode) {
      case 'edit':
        return 'Rule';
      case 'new':
        return 'New rule';
      default:
        return 'Suggested rule';
    }
  });
  readonly primaryActionLabel = computed(() => {
    if (this.proposal()?.action === 'update') return 'Update rule';
    switch (this.mode) {
      case 'edit':
        return 'Save changes';
      case 'new':
        return 'Create rule';
      default:
        return 'Save rule';
    }
  });
  readonly saveLine = computed(() => {
    const bucketName = this.selectedBucketName();
    if (!bucketName) return null;
    if (this.proposal()?.action === 'update')
      return `Updating sorts unsorted matching transactions into ${bucketName}. Transactions you already categorized are not moved.`;
    if (this.mode === 'edit') {
      const count = this.backfillCount();
      const enabled = this.rule()?.enabled ?? true;
      const base = enabled
        ? 'Saving keeps the rule on for future transactions.'
        : 'Saving keeps the rule paused. Resume it to sort future transactions.';
      if (!enabled || count == null || count === 0) return base;
      return `${base} ${pluralize(count, 'unsorted transaction')} currently match and will be sorted into ${bucketName}. Transactions you already categorized are not moved.`;
    }
    const includingThis = this.transactionSortable() && !this.transactionExcludedByEdit() ? ', including this one' : '';
    return `Saving turns the rule on for future transactions and sorts unsorted matching transactions into ${bucketName}${includingThis}. Transactions you already categorized are not moved.`;
  });

  constructor() {
    const data = this.data;

    this.mode = resolveMode(data);

    effect(() => {
      if (untracked(() => this.formSeeded())) return;
      const seed = this.seedForm();
      if (!seed) return;
      untracked(() => {
        this.suggestionSnapshot.set(this.suggestion());
        this.form.set(seed);
        this.formSeeded.set(true);
      });
    });
  }

  private seedForm(): RuleFormState | null {
    if (this.mode === 'edit') {
      const rule = this.rule();
      if (!rule) return null;
      return {
        merchant: rule.merchant,
        transactionType: rule.transactionType,
        amountMode: rule.amountMin == null ? 'any' : 'range',
        amountMin: rule.amountMin,
        amountMax: rule.amountMax,
        bucketId: rule.bucketId,
      };
    }
    if (this.data.suggestionId) {
      const suggestion = this.suggestion();
      if (!suggestion) return null;
      return {
        merchant: suggestion.merchant,
        transactionType: suggestion.transactionType,
        amountMode:
          (suggestion.proposal ? suggestion.proposal.amountMin : suggestion.amountMin) == null ? 'any' : 'range',
        amountMin: suggestion.proposal ? suggestion.proposal.amountMin : suggestion.amountMin,
        amountMax: suggestion.proposal ? suggestion.proposal.amountMax : suggestion.amountMax,
        bucketId: suggestion.bucketId,
      };
    }
    if (this.data.transactionId) {
      const transaction = this.transaction();
      if (!transaction) return null;
      return { ...EMPTY_FORM, merchant: transaction.merchant, transactionType: transactionRuleType(transaction) };
    }
    return EMPTY_FORM;
  }

  closeDrawer(): void {
    this.drawer.closeDrawer();
  }

  patchForm(patch: Partial<RuleFormState>): void {
    this.form.update(form => ({ ...form, ...patch }));
    this.conflict.set(null);
    this.errorMessage.set(null);
    this.amountError.set(null);
  }

  setTransactionType(type: CategorizationRuleTransactionType | null): void {
    if (type == null || this.form().transactionType === type) return;
    this.patchForm({ transactionType: type, bucketId: null });
  }

  setAmountMode(mode: AmountMode | null): void {
    if (mode == null || this.form().amountMode === mode) return;
    this.patchForm({ amountMode: mode });
  }

  onMerchantInput(value: string): void {
    this.patchForm({ merchant: value });
    this.merchantListOpen.set(true);
    this.merchantSearch.next(value);
  }

  selectMerchant(merchant: MerchantSuggestion): void {
    this.pickedMerchant.set(merchant);
    this.patchForm({ merchant: merchant.name });
    this.merchantListOpen.set(false);
  }

  onMerchantBlur(): void {
    // Delay so a click on a match registers before the list closes.
    setTimeout(() => this.merchantListOpen.set(false), 150);
  }

  onBucketChange(option: UiComboboxOption<Bucket> | null): void {
    this.patchForm({ bucketId: option?.value.id ?? null });
  }

  typeLabel(type: CategorizationRuleTransactionType): string {
    return ruleTypeLabel(type);
  }

  bucketName(bucketId: string): string {
    return this.bucketsById().get(bucketId)?.name ?? 'Budget';
  }

  evidenceSummary(): string | null {
    const suggestion = this.suggestion();
    if (!suggestion) return null;
    const noun = suggestion.transactionType === 'INCOME' ? 'deposits' : 'purchases';
    return `Your last ${suggestion.evidence.transactionCount} manually sorted ${suggestion.merchant} ${noun} all went to ${this.bucketName(suggestion.bucketId)}.`;
  }

  evidenceAmountLine(): string | null {
    const suggestion = this.suggestion();
    if (!suggestion) return null;
    const observed = `${formatRuleAmount(suggestion.evidence.amountMin)} – ${formatRuleAmount(suggestion.evidence.amountMax)}`;
    // The update/review card above already states the change; this line only reports the evidence.
    if (suggestion.proposal?.action === 'update') return `Amounts ranged ${observed}.`;
    if (suggestion.amountMin == null || suggestion.amountMax == null)
      return `Amounts ranged ${observed}, so no amount limit was proposed.`;

    const label = amountRangeLabel(suggestion);
    return `Amounts ranged ${observed}, so the proposed range is ${label.charAt(0).toLowerCase()}${label.slice(1)}.`;
  }

  pausedExplanation(): string | null {
    const rule = this.rule();
    const state = this.ruleState();
    if (!rule || !state || state === 'active') return null;
    const subject = `${rule.merchant} ${rule.transactionType === 'INCOME' ? 'deposits' : 'expenses'}`;
    const range =
      rule.amountMin == null
        ? ''
        : ` between ${formatRuleAmount(rule.amountMin)} and ${formatRuleAmount(rule.amountMax ?? rule.amountMin)}`;
    const already = `Transactions this rule already sorted stay in ${this.bucketName(rule.bucketId)}.`;
    if (state === 'paused') return `While paused, ${subject}${range} arrive uncategorized. ${already}`;
    if (state === 'bucket-archived')
      return `${this.bucketName(rule.bucketId)} is archived, so ${subject}${range} arrive uncategorized until you pick another budget. ${already}`;
    return `The budget this rule pointed to no longer exists, so ${subject}${range} arrive uncategorized until you pick another budget.`;
  }

  conflictDescription(): string {
    const conflict = this.conflict();
    const rule = conflict?.conflictingRule;
    if (!rule)
      return 'An active rule already covers this merchant, type, and amount range. Narrow the amounts here, or open that rule instead.';
    const noun = rule.transactionType === 'INCOME' ? 'deposits' : 'expenses';
    const amounts =
      rule.amountMin == null
        ? 'of any amount'
        : `between ${formatRuleAmount(rule.amountMin)} and ${formatRuleAmount(rule.amountMax ?? rule.amountMin)}`;
    return `An active rule already sorts ${rule.merchant} ${noun} ${amounts} into ${this.bucketName(rule.bucketId)}. Narrow the amounts here, or open that rule instead.`;
  }

  openConflictingRule(): void {
    const conflict = this.conflict();
    if (!conflict?.conflictingRuleId) return;
    this.drawer.openTransactionRuleDrawer({ ruleId: conflict.conflictingRuleId });
  }

  async save(): Promise<void> {
    if (!this.formValid() || this.saving()) {
      if (this.form().amountMode === 'range')
        this.amountError.set('Enter a minimum and maximum amount, with the minimum first.');
      return;
    }
    this.saving.set(true);
    this.conflict.set(null);
    this.errorMessage.set(null);
    try {
      const rule = await this.persistRule();
      const backfill = rule.enabled ? await this.applyBackfill(rule) : { sorted: 0, failed: false };
      // One message: the rule is saved either way, and a failed backfill must not read as success.
      if (backfill.failed) {
        this.toast.open(
          `${this.savedHead()}, but existing transactions could not be sorted. New ones will still be.`,
          'OK',
          {
            duration: 7000,
          },
        );
      } else if (this.proposal()?.action === 'update')
        sonner.success(this.savedMessage(rule, backfill.sorted), { duration: 7000 });
      else if (rule.enabled) {
        // Pause is the undo the design offers: nothing un-sorts the backfilled
        // transactions, but the rule can stop applying to future ones right away.
        // Sonner owns action-capable toasts; the legacy banner stays for plain confirmations.
        sonner.success(this.savedMessage(rule, backfill.sorted), {
          duration: 7000,
          action: {
            label: 'Pause',
            onClick: () => {
              void this.pauseFromToast(rule);
            },
          },
        });
      } else {
        // Already paused: nothing to undo, so no action.
        sonner.success(this.savedMessage(rule, backfill.sorted), { duration: 7000 });
      }
      this.closeDrawer();
    } catch (error) {
      this.handleSaveError(error);
    } finally {
      this.saving.set(false);
    }
  }

  private async persistRule(): Promise<CategorizationRule> {
    const criteria = this.criteria();
    const bucketId = this.form().bucketId ?? '';
    if (this.mode === 'edit') {
      const rule = this.rule();
      if (!rule) throw new Error('Rule is no longer available.');
      // The merchant is fixed once a rule exists; only type, amounts, and budget change.
      const { merchant: _merchant, ...editableCriteria } = criteria;
      const updated = await this.updateMutation.mutateAsync({
        ruleId: rule.id,
        patch: { ...editableCriteria, bucketId },
      });
      if (!updated) throw new Error('Rule is no longer available.');
      return updated;
    }
    const suggestion = this.suggestion();
    if (suggestion?.proposal && (suggestion.proposal.action === 'update' || !this.formChangedFromSuggestion())) {
      return this.acceptMutation.mutateAsync({
        suggestionId: suggestion.id,
        expectedProposalToken: suggestion.proposal.token,
      });
    }

    return this.createMutation.mutateAsync({
      ...criteria,
      bucketId,
      enabled: true,
      sourceSuggestionId: suggestion?.id ?? null,
    });
  }

  private async applyBackfill(rule: CategorizationRule): Promise<{ sorted: number; failed: boolean }> {
    try {
      const result = await this.applyMutation.mutateAsync(rule.id);
      if (result.categorizedCount > 0) this.sessionService.adjustUnsortedTransactionsCount(-result.categorizedCount);
      return { sorted: result.categorizedCount, failed: false };
    } catch {
      return { sorted: 0, failed: true };
    }
  }

  private async pauseFromToast(rule: CategorizationRule): Promise<void> {
    const retry = `We couldn't pause the ${rule.merchant} rule. Open it from the rules page to try again.`;
    try {
      const paused = await this.categorizationRuleService.pauseRule(rule.id);
      // A null result means the rule was gone by the time Pause was pressed.
      this.toast.open(paused ? `${rule.merchant} rule paused. Transactions it already sorted stay put.` : retry);
    } catch {
      this.toast.open(retry);
    }
  }

  private savedHead(): string {
    return this.mode === 'edit' || this.proposal()?.action === 'update' ? 'Rule updated' : 'Rule saved';
  }

  private savedMessage(rule: CategorizationRule, sorted: number): string {
    const bucketName = this.bucketName(rule.bucketId);
    const remaining = this.mode.startsWith('suggestion')
      ? Math.max(0, (this.suggestionsQuery.data()?.length ?? 1) - 1)
      : null;
    const head = this.savedHead();
    const sortedText = sorted > 0 ? ` · ${pluralize(sorted, 'transaction')} sorted into ${bucketName}` : '';
    const remainingText = remaining != null && remaining > 0 ? ` · ${pluralize(remaining, 'suggestion')} left` : '';
    return `${head}${sortedText}${remainingText}`;
  }

  private handleSaveError(error: unknown): void {
    if (
      error instanceof HttpErrorResponse &&
      error.status === 409 &&
      hasApiErrorCode(error, 'RULE_SUGGESTION_CHANGED')
    ) {
      this.suggestionStale.set(true);
      return;
    }
    const conflict = ruleConflictDetailsFromError(error);
    if (conflict) {
      this.conflict.set(conflict);
      return;
    }
    const message = error instanceof HttpErrorResponse ? getApiErrorMessage(error) : null;
    this.errorMessage.set(message ?? "We couldn't save this rule. Try again in a moment.");
  }

  openSuggestedRule(ruleId: string): void {
    this.drawer.openTransactionRuleDrawer({ ruleId });
  }

  proposalRange(min: number | null, max: number | null): string {
    return amountRangeLabel({ amountMin: min, amountMax: max });
  }

  async refreshSuggestion(): Promise<void> {
    const previous = this.suggestion();
    const refreshed = await this.suggestionsQuery.refetch();
    // Keep the stale note (and current state) so a failed refresh can be retried.
    if (refreshed.isError) {
      this.errorMessage.set("We couldn't refresh this suggestion. Try again in a moment.");
      return;
    }
    const latest = refreshed.data?.find(
      row =>
        merchantKey(row.merchant) === merchantKey(previous?.merchant ?? '') &&
        row.transactionType === previous?.transactionType,
    );
    // The server hides a suggestion while it recomputes it, and an empty list can't tell
    // "still updating" from "gone". Keep what the user reviewed and the Refresh action;
    // the note tells them they can also close and check back.
    if (!latest) {
      this.suggestionUpdating.set(true);
      return;
    }
    this.suggestionUpdating.set(false);
    this.suggestionSnapshot.set(latest);
    this.formSeeded.set(false);
    this.suggestionStale.set(false);
    this.errorMessage.set(null);
  }

  async dismiss(): Promise<void> {
    const suggestion = this.suggestion();
    if (!suggestion || this.saving()) return;
    this.saving.set(true);
    try {
      await this.dismissMutation.mutateAsync(suggestion.id);
      this.toast.open(`${suggestion.merchant} suggestion dismissed. Restore it from the rules page any time.`);
      this.closeDrawer();
    } catch {
      this.errorMessage.set("We couldn't dismiss this suggestion. Try again in a moment.");
    } finally {
      this.saving.set(false);
    }
  }

  async togglePaused(): Promise<void> {
    const rule = this.rule();
    if (!rule || this.saving()) return;
    this.saving.set(true);
    this.conflict.set(null);
    this.errorMessage.set(null);
    try {
      await this.updateMutation.mutateAsync({ ruleId: rule.id, patch: { enabled: !rule.enabled } });
      this.toast.open(rule.enabled ? `${rule.merchant} rule paused.` : `${rule.merchant} rule resumed.`);
    } catch (error) {
      this.handleSaveError(error);
    } finally {
      this.saving.set(false);
    }
  }

  confirmDelete(): void {
    const rule = this.rule();
    if (!rule) return;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `Delete the ${rule.merchant} rule? Transactions it already sorted stay where they are.`,
        cancelText: 'Cancel',
        confirmText: 'Delete',
        confirmColor: 'warn',
        onConfirm: () => {
          void this.deleteRule(rule);
        },
      },
    });
  }

  private async deleteRule(rule: CategorizationRule): Promise<void> {
    this.saving.set(true);
    try {
      await this.updateMutation.mutateAsync({ ruleId: rule.id, patch: { deleted: true } });
      this.toast.open(`${rule.merchant} rule deleted.`);
      this.closeDrawer();
    } catch (error) {
      this.handleSaveError(error);
    } finally {
      this.saving.set(false);
    }
  }

  readonly createMutation = injectMutation(() => this.categorizationRuleService.createRuleMutationOptions());
  readonly updateMutation = injectMutation(() => this.categorizationRuleService.updateRuleMutationOptions());
  readonly acceptMutation = injectMutation(() => this.categorizationRuleService.acceptSuggestionMutationOptions());
  readonly dismissMutation = injectMutation(() => this.categorizationRuleService.dismissSuggestionMutationOptions());
  readonly applyMutation = injectMutation(() => this.categorizationRuleService.applyBackfillMutationOptions());
}

function resolveMode(data: TransactionRuleDrawerData): TransactionRuleDrawerMode {
  if (data.ruleId) return 'edit';
  if (data.suggestionId) return data.transactionId ? 'suggestion-from-transaction' : 'suggestion';
  return 'new';
}
